import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@shared/services/api';
import { queryKeys } from '@shared/lib/queryKeys';
import { Note } from '@shared/types';
import { toast } from '@shared/lib/toast';
import { offlineDataManager } from '@shared/lib/offlineData';
import { offlineQueueManager } from '@shared/lib/offlineQueue';

// Generate a client-side id for records created while offline. These are
// remapped to the server-assigned id during a manual sync (see useOfflineSync).
const genLocalId = (): string => {
  const rand = typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}_${Math.random().toString(36).slice(2)}`;
  return `local-${rand}`;
};

// Offline-first query: serve notes from local storage only. The network is
// touched exclusively during a manual sync, which updates this cache directly.
export const useNotesQuery = () => {
  return useQuery({
    queryKey: queryKeys.notes.list(),
    queryFn: async () => offlineDataManager.getNotes(),
    staleTime: Infinity,
    gcTime: 10 * 60 * 1000,
    enabled: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    networkMode: 'always',
    retry: false,
  });
};

// Offline-first: always write to local storage + the sync queue, never the API.
export const useCreateOrUpdateNoteMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (noteData: Partial<Note>) => {
      // Ensure status is always 'ACTIVE' or 'INACTIVE', never empty string
      const status = noteData.status && (noteData.status === 'ACTIVE' || noteData.status === 'INACTIVE')
        ? noteData.status
        : 'ACTIVE';

      // New notes get a client-side id so they exist locally before syncing.
      const isNew = !noteData.note_id || String(noteData.note_id).startsWith('local-');
      const note_id = noteData.note_id && String(noteData.note_id).length > 0
        ? String(noteData.note_id)
        : genLocalId();
      const now = new Date().toISOString();

      const localNote: Note = {
        ...(noteData as Note),
        note_id,
        status,
        sequence: noteData.sequence || 1,
        created_at: (noteData as Note).created_at || now,
        updated_at: now,
      };

      // Persist locally (source of truth)
      const alreadyStored = offlineDataManager.getNotes().some(n => n.note_id === note_id);
      if (alreadyStored) {
        offlineDataManager.updateNote(note_id, localNote);
      } else {
        offlineDataManager.addNote(localNote);
      }

      // Queue for the next manual sync
      offlineQueueManager.addOperation({
        type: isNew ? 'CREATE' : 'UPDATE',
        data: localNote,
        maxRetries: 3,
      });

      return localNote;
    },
    onSuccess: (newNote) => {
      // Update the notes list in cache
      queryClient.setQueryData(queryKeys.notes.list(), (oldNotes: Note[] | undefined) => {
        if (!oldNotes) return [newNote];

        const existingIndex = oldNotes.findIndex(note => note.note_id === newNote.note_id);
        if (existingIndex >= 0) {
          // Update existing note
          const updatedNotes = [...oldNotes];
          updatedNotes[existingIndex] = newNote;
          return updatedNotes;
        } else {
          // Add new note
          return [newNote, ...oldNotes];
        }
      });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to save note');
    },
  });
};

// Mutation hook for note sync via WebSocket
export const useNoteSyncMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: any) => {
      return await api.sendNoteSync(payload);
    },
    onSuccess: (syncedNote) => {
      // Update the notes list in cache
      queryClient.setQueryData(queryKeys.notes.list(), (oldNotes: Note[] | undefined) => {
        if (!oldNotes) return [syncedNote];
        
        const existingIndex = oldNotes.findIndex(note => note.note_id === syncedNote.note_id);
        if (existingIndex >= 0) {
          // Update existing note
          const updatedNotes = [...oldNotes];
          updatedNotes[existingIndex] = syncedNote;
          return updatedNotes;
        } else {
          // Add new note
          return [syncedNote, ...oldNotes];
        }
      });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to sync note');
    },
  });
};

// Mutation hook for grammar check
export const useGrammarCheckMutation = () => {
  return useMutation({
    mutationFn: async ({ html, noteId }: { html: string, noteId: string }) => {
      return await api.checkGrammar({ html, noteId });
    },
    onSuccess: (correctedHtml) => {
      toast.success('Grammar check completed');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Grammar check failed');
    },
  });
};

// Optimistic update hook for immediate UI updates
export const useOptimisticNoteUpdate = () => {
  const queryClient = useQueryClient();

  const updateNoteOptimistically = (noteId: string, updates: Partial<Note>) => {
    queryClient.setQueryData(queryKeys.notes.list(), (oldNotes: Note[] | undefined) => {
      if (!oldNotes) return oldNotes;
      
      return oldNotes.map(note => 
        note.note_id === noteId 
          ? { ...note, ...updates }
          : note
      );
    });
  };

  const addNoteOptimistically = (note: Note) => {
    // Ensure the note has required fields with defaults
    const noteWithDefaults = {
      ...note,
      status: note.status || 'ACTIVE',
      sequence: note.sequence || 1,
    };
    
    queryClient.setQueryData(queryKeys.notes.list(), (oldNotes: Note[] | undefined) => {
      if (!oldNotes) return [noteWithDefaults];
      return [noteWithDefaults, ...oldNotes];
    });
  };

  const removeNoteOptimistically = (noteId: string) => {
    queryClient.setQueryData(queryKeys.notes.list(), (oldNotes: Note[] | undefined) => {
      if (!oldNotes) return oldNotes;
      return oldNotes.filter(note => note.note_id !== noteId);
    });
  };

  return {
    updateNoteOptimistically,
    addNoteOptimistically,
    removeNoteOptimistically,
  };
};
